'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// UI lib components
import {
  ActionIcon,
  Box,
  Center,
  Group as GroupComponent,
  Tooltip,
} from '@mantine/core';

// API & Types
import {
  Group,
  Permission,
  ResourceAccess,
  ResourceAccessUpdateInput,
  useGetGroupsQuery,
  useUpdateGroupsResourceAccessMutation,
} from '@app-types/blackroom-api';
import { SectionContentProps } from '../types';

// Style
import { sectionPermissionsGrid } from './styles/index.css';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { Fragment, useEffect, useState } from 'react';
import { palette } from '@app/shared/ui-kit/colors';
import { useTranslation } from 'react-i18next';
import { IconLoader2 } from '@tabler/icons-react';
import { QueriedGroup } from '@app/features/Group/types';
import useGroupDisplayName from '@app/features/Group/hooks/useGroupDisplayName';
import {
  getPermissionBorderColor,
  getPermissionColor,
  getPermissionVariant,
  getTooltipLabel,
} from '@app/features/Permission/utils/permissionUtilities';
import renderPermissionIcon from '@app/features/Permission/utils/renderPermissionIcon';
import updatePermissionsCache from '@app/features/Permission/utils/updatePermissionsCache';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function SectionPermissions({ resource }: SectionContentProps) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();
  const { t } = useTranslation();
  const { getDisplayName } = useGroupDisplayName();

  const [updateGroupsResourceAccess] = useUpdateGroupsResourceAccessMutation();

  const { data: groupsData, loading: loadingGroups } = useGetGroupsQuery({
    variables: { filter: { workspaceId: workspace.id } },
    fetchPolicy: 'network-only',
  });

  const [groups, setGroups] = useState(groupsData?.groups.nodes ?? []);

  useEffect(() => {
    const groupNodes = groupsData?.groups.nodes;
    if (groupNodes) {
      setGroups(groupNodes);
    }
  }, [groupsData]);

  const updatePermissions = (
    access: ResourceAccessUpdateInput,
    groupId: string,
    existingAccess?: ResourceAccess,
  ) => {
    updateGroupsResourceAccess({
      variables: {
        updateGroupResourceAccessInput: {
          groupIds: [groupId],
          resourceAccess: [access],
        },
      },
      update: (cache, { data }) => {
        const updatedResourceAccesses = data?.updateGroupResourceAccess;

        const updatedResourceAccess = updatedResourceAccesses?.find(
          (resourceAccess) => resourceAccess.resourceId === access.resourceId,
        );

        updatePermissionsCache(
          cache,
          updatedResourceAccess!,
          access.removedPermissions ?? [],
          existingAccess,
        );
      },
    });
  };

  const renderGroupLabel = (
    group: Pick<Group, 'name' | 'color'>,
    active: boolean,
  ) => (
    <GroupComponent gap={10} opacity={active ? 1 : 0.4} wrap="nowrap">
      <Box
        w={8}
        h={8}
        style={{
          borderRadius: '100%',
          border: `1px solid ${group.color}`,
          background: active ? group.color : undefined,
          flexShrink: 0,
        }}
      />
      <Box c={group.color} style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
        {getDisplayName(group)}
      </Box>
    </GroupComponent>
  );

  // These fields may not be available when using prefilled resource from table row
  const accesses = resource.accesses ?? [];
  const inheritedAccesses = resource.inheritedAccesses ?? [];
  const descendantsAccesses = resource.descendantsAccesses ?? [];

  const isGranted = (permission: Permission, groupId: string) => {
    return !!accesses.find(
      (ia) =>
        !!ia.permissions.find((p) => p === permission) &&
        ia.groupId === groupId,
    );
  };

  const isInherited = (permission: Permission, groupId: string) => {
    return !!inheritedAccesses.find(
      (ia) =>
        !!ia.permissions.find((p) => p === permission) &&
        ia.groupId === groupId,
    );
  };

  const isMixed = (permission: Permission, groupId: string) => {
    return !!descendantsAccesses.find(
      (da) =>
        !!da.permissions.find((p) => p === permission) &&
        da.groupId === groupId,
    );
  };

  const renderGroupPermissions = (group: QueriedGroup) => {
    return Object.values(Permission).map((permission) => {
      const isGrantedPermission = isGranted(permission, group.id);
      const isInheritedPermission = isInherited(permission, group.id);
      const isMixedPermission = isMixed(permission, group.id);

      const hasViewPermission = [
        Permission.ResourceAccessOriginal,
        Permission.ResourceAccessRedacted,
      ].some(
        (permission) =>
          isGranted(permission, group.id) || isInherited(permission, group.id),
      );

      const variant = getPermissionVariant(
        isGrantedPermission,
        isInheritedPermission,
      );
      const color = getPermissionColor(isGrantedPermission, isMixedPermission);
      const borderColor = getPermissionBorderColor(
        isGrantedPermission,
        isInheritedPermission,
        isMixedPermission,
      );
      const tooltipLabel = getTooltipLabel(
        t,
        permission,
        isGrantedPermission,
        isInheritedPermission,
        isMixedPermission,
        hasViewPermission,
      );

      return (
        <Tooltip key={permission} label={tooltipLabel}>
          <ActionIcon
            variant={variant}
            color={color}
            bg={
              permission === Permission.ResourceDownload && !hasViewPermission
                ? 'transparent'
                : undefined
            }
            style={{
              border: `1px solid ${borderColor}`,
            }}
            disabled={
              isInheritedPermission ||
              (permission === Permission.ResourceDownload &&
                !hasViewPermission &&
                !isMixedPermission)
            }
            onClick={() => {
              const existingAccess = accesses.find(
                (ra) => ra.groupId === group.id,
              );

              // remove permission
              if (isGrantedPermission || isMixedPermission) {
                updatePermissions(
                  {
                    resourceId: resource.id,
                    addedPermissions: [],
                    removedPermissions: [permission],
                  },
                  group.id,
                  existingAccess,
                );
              }
              // add permission
              else if (!isGrantedPermission) {
                updatePermissions(
                  {
                    resourceId: resource.id,
                    addedPermissions: [permission],
                    removedPermissions: [],
                  },
                  group.id,
                  existingAccess,
                );
              }
            }}
          >
            {renderPermissionIcon(permission, { size: 16 })}
          </ActionIcon>
        </Tooltip>
      );
    });
  };

  /* ******************************** RENDERING ******************************* */
  return loadingGroups ? (
    <Center w="100%">
      <IconLoader2 color={palette.blackroomGray} />
    </Center>
  ) : !groups?.length ? (
    <Center w="100%">
      <Center
        p="xs"
        c={palette.blackroomLightGray5}
        fz="sm"
        fw="bold"
        w="100%"
        maw={240}
        ta="center"
      >
        {t('Group:PageGroups.noGroups')}
      </Center>
    </Center>
  ) : (
    <Box className={sectionPermissionsGrid} px="lg" w="100%">
      {groups?.map((group) => {
        const isGroupActive = Object.values(Permission).some(
          (permission) =>
            isGranted(permission, group.id) ||
            isInherited(permission, group.id) ||
            isMixed(permission, group.id),
        );

        return (
          <Fragment key={group.id}>
            <GroupComponent gap={5}>
              {renderGroupLabel(group, isGroupActive)}
            </GroupComponent>

            <GroupComponent gap={5} wrap="nowrap">
              {renderGroupPermissions(group)}
            </GroupComponent>
          </Fragment>
        );
      })}
    </Box>
  );
}

export default SectionPermissions;
